/* Personal Accident Policy Renewal - 2 Screen Flow */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import PersonalAccidentRenewalSkeleton from "./components/PersonalAccidentRenewalSkeleton";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import {
  policyView,
  getPolicyViewResponse,
  getPolicyViewLoading,
} from "../../../redux/features/policyDetailsSlice";
import { getClientSource } from "../../../utils/getClientSource";
import PolicyDetailsPage from "./steps/PolicyDetailsPage";
import ReviewConfirmPage from "./steps/ReviewConfirmPage";

const RenewalPersonalAccidentPolicy = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const translate = useLanguageTranslate();

  // Current step state (0 = Policy Details, 1 = Review and Confirm)
  const [currentStep, setCurrentStep] = useState(0);

  // Form data state for the flow
  const [formData, setFormData] = useState({
    policyLimit: "",
    occupation: "",
    originalPolicyLimit: "",
  });

  // Premium data state (shared between steps)
  const [premiumData, setPremiumData] = useState(null);
  const [premiumError, setPremiumError] = useState(null);

  // Get selected policy from location state (passed from GenericSelectPoliciesPage)
  const selectedPolicy = location.state?.selectedPolicy;

  // Get policy view data from Redux
  const policyInfo = useSelector(getPolicyViewResponse);
  const isPolicyViewLoading = useSelector(getPolicyViewLoading);

  // Initialize and call policyView API on mount
  useEffect(() => {
    if (selectedPolicy) {
      const polReq = {
        PolicyNumber: selectedPolicy.PolicyNumber,
        Channel: getClientSource(),
      };

      // Fetch policy details if not already loaded or different policy
      if (
        !policyInfo?.PolicyHeader ||
        policyInfo?.PolicyHeader?.PolicyNumber !== selectedPolicy.PolicyNumber
      ) {
        dispatch(policyView(polReq));
      }
    }
  }, []);

  // If no selected policy, redirect back to renewal hub
  useEffect(() => {
    if (!selectedPolicy && !policyInfo?.PolicyHeader) {
      navigate("/renew");
    }
  }, [selectedPolicy, policyInfo, navigate]);

  // Get policy details from PolicyHeader
  const policyDetailsInfo = policyInfo?.PolicyHeader;

  // Handle navigation between steps
  const goToNextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 1));
  };

  const goToPreviousStep = () => {
    if (currentStep === 0) {
      // If on first step, go back to renewal hub
      navigate("/renew");
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleCloseFlow = () => {
    navigate("/renew");
  };

  // Show skeleton while policy view is loading
  if (isPolicyViewLoading) {
    return <PersonalAccidentRenewalSkeleton />;
  }

  // Render step components based on current step
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <PolicyDetailsPage
            policyDetailsInfo={policyDetailsInfo}
            onContinue={goToNextStep}
            onBack={goToPreviousStep}
            formData={formData}
            setFormData={setFormData}
            premiumData={premiumData}
            setPremiumData={setPremiumData}
            premiumError={premiumError}
            setPremiumError={setPremiumError}
          />
        );
      case 1:
        return (
          <ReviewConfirmPage
            policyDetailsInfo={policyDetailsInfo}
            formData={formData}
            onBack={goToPreviousStep}
            premiumData={premiumData}
            premiumError={premiumError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderStep()}
      <div id="home-footer-portal" />
    </>
  );
};

export default RenewalPersonalAccidentPolicy;
